/**
 * @module components/Progress
 * @description Funding progress bar + raised/goal, donors and days left
 * (brief §4.4). Reused by cards, featured cards and Quick View.
 */
import type { JSX } from "react";
import type { SampleFunding } from "../domain/types";
import { daysLeft, formatCount, formatUsd, percentRaised } from "../lib/format";
import "./Progress.css";

/** Props for <Progress>. */
interface ProgressProps {
    funding: SampleFunding;
    /** "compact" hides the donors/days row (list rows, cart). */
    variant?: "full" | "compact";
    /** Inverse colors on dark backgrounds (featured cards). */
    inverse?: boolean;
}

/** Progress bar with an accessible meter role. */
export function Progress({ funding, variant = "full", inverse = false }: ProgressProps): JSX.Element {
    const pct: number = percentRaised(funding.raisedUsd, funding.goalUsd);
    const days: number = daysLeft(funding.endsAt);
    return (
        <div className={`progress${inverse ? " progress--inverse" : ""}`}>
            <div className="progress__figures">
                <span><strong>{formatUsd(funding.raisedUsd)}</strong> raised of {formatUsd(funding.goalUsd)}</span>
                <span className="progress__pct">{pct}%</span>
            </div>
            <div className="progress__track" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={`${pct}% of goal raised (sample figures)`}>
                <div className="progress__fill" style={{ width: `${pct}%` }} />
            </div>
            {variant === "full" && (
                <div className="progress__meta">
                    <span>{formatCount(funding.donorCount)} donors</span>
                    <span>{days === 0 ? "Final day" : `${days} days left`}</span>
                </div>
            )}
        </div>
    );
}
